import React, { useState, useMemo, useEffect, useRef } from "react";
import { Layout, Menu, Typography, Button, Dropdown, Space, Spin, Tag, Breadcrumb, Avatar, Badge, AutoComplete, Input, Drawer } from "antd";
import {
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  BellOutlined,
  SearchOutlined,
  MenuOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useUserContext } from "Hooks/UserContext";
import { useNotify } from "Hooks/NotificationContext";
import { useApi } from "Hooks/ServicesContext";
import { Paths } from "Router/Paths";
import { MESSAGES, depoNotificationSuccessConf, depoNotificationErrorConf, PATH, MODULE_SLUGS } from "Helpers/ats.constants";
import { getFullImageUrl } from "Helpers/functions";
import AppRoutes from "Router/Routes";

const { Header, Sider, Content } = Layout;

const SIDEBAR_WIDTH = 232;
const SIDEBAR_COLLAPSED = 72;
const HEADER_HEIGHT = 52;
const MOBILE_BREAKPOINT = 992;

function generateBreadcrumbs(pathname) {
  const basePath = `/${PATH}`;
  if (!pathname.startsWith(basePath)) return [];

  const segments = pathname.replace(basePath, "").split("/").filter(Boolean);
  const items = [
    {
      title: (
        <Link to={Paths.dashboard}>
          <HomeOutlined />
        </Link>
      ),
    },
  ];

  let currentPath = basePath;
  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = segment
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({ title: label });
  });

  return items;
}

const groupLabelStyle = {
  fontSize: 10,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "1.2px",
  fontWeight: 600,
  padding: "16px 16px 4px",
  lineHeight: "16px",
};

export default function AppLayout() {
  const {
    loggedIn,
    loader,
    userDetails,
    filteredMenuList,
    setLoggedIn,
    setuserDetails,
  } = useUserContext();
  const { depoNotify } = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const apiService = useApi();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const searchRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );

  // Track window width for mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      if (window.innerWidth >= MOBILE_BREAKPOINT) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Depots this operator can switch between (multi-depot users). The
  // active depot lives in the JWT, so switching re-issues the token
  // server-side and we reload the app config to pick up the new depot
  // and its per-depot permissions.
  const { data: myDepotsData } = useQuery(
    "my-depots",
    () => apiService.getMyDepots(),
    { enabled: loggedIn && !loader, retry: 1, onError: () => {} }
  );
  const myDepots = myDepotsData?.data || [];

  const handleSwitchDepot = async (code) => {
    if (code === userDetails?.depot?.depot_code) return;
    try {
      const res = await apiService.switchDepot(code);
      if (res?.success) {
        // New depot => different data scope (and possibly permissions).
        // A soft cache-clear left some list pages showing the previous
        // depot's rows until a manual refresh, so do a HARD reload to the
        // dashboard: the new token is used for every request, react-query
        // starts empty, and the app config reloads on boot — guaranteeing
        // none of the old depot's transactions/data linger.
        queryClient.clear();
        window.location.assign(Paths.dashboard);
      }
    } catch (e) {
      depoNotify("Failed to switch depot", depoNotificationErrorConf);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.getLogoutUser();
    } catch (e) {
      // ignore
    }
    localStorage.clear();
    // Wipe the react-query cache so the next session starts fresh —
    // otherwise cached list results (keyed by the previous user's
    // search terms / filters) bleed into the new login and the
    // operator sees stale data + pre-filled search values.
    queryClient.clear();
    setLoggedIn(false);
    setuserDetails(null);
    depoNotify(MESSAGES.LOGOUT_SUCCESS, depoNotificationSuccessConf);
    navigate(Paths.base);
  };

  const breadcrumbItems = useMemo(
    () => generateBreadcrumbs(location.pathname),
    [location.pathname]
  );

  // Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Stock alerts — only poll when the user actually has the
  // stock-levels module permission. Otherwise the global onError
  // middleware shows a "not allowed to access" toast every refetch
  // interval, even though this is just a sidebar badge counter.
  const hasStockLevelsAccess = useMemo(() => {
    const mods = userDetails?.module_permissions;
    if (!Array.isArray(mods)) return false;
    return mods.some((m) => m.module_slug === MODULE_SLUGS.STOCK_LEVELS);
  }, [userDetails]);

  const { data: stockAlertsData } = useQuery(
    "stockAlertsCount",
    () => apiService.getStockAlerts(),
    {
      enabled: loggedIn && !loader && hasStockLevelsAccess,
      refetchInterval: 300000,
      retry: 1,
      onError: () => {},
    }
  );

  const alertCount = useMemo(() => {
    if (!stockAlertsData?.data) return 0;
    const alerts = stockAlertsData.data;
    if (Array.isArray(alerts)) return alerts.length;
    if (typeof alerts.count === "number") return alerts.count;
    if (typeof alerts.total === "number") return alerts.total;
    return 0;
  }, [stockAlertsData]);

  // Build grouped menu (must be before early returns to keep hooks order stable)
  const menuItems = useMemo(() => {
    const items = [];
    let currentGroup = null;
    (filteredMenuList || []).forEach((item) => {
      if (item.group !== currentGroup) {
        currentGroup = item.group;
        items.push({
          type: "group",
          label: <span style={groupLabelStyle}>{item.group}</span>,
          children: [],
        });
      }
      const group = items[items.length - 1];
      group.children.push({
        key: item.path,
        icon: item.icon ? React.createElement(item.icon) : null,
        label: item.title,
      });
    });
    return items;
  }, [filteredMenuList]);

  const filteredMenuItems = useMemo(() => {
    if (!menuSearch.trim()) return menuItems;
    const term = menuSearch.toLowerCase();
    const filtered = [];
    menuItems.forEach((group) => {
      if (group.type === "group" && group.children) {
        const matchedChildren = group.children.filter((child) =>
          child.label?.toLowerCase?.().includes(term)
        );
        if (matchedChildren.length > 0) {
          filtered.push({ ...group, children: matchedChildren });
        }
      }
    });
    return filtered;
  }, [menuItems, menuSearch]);

  if (loader) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!loggedIn) {
    return <AppRoutes loggedIn={loggedIn} />;
  }

  const handleMenuClick = (e) => {
    navigate(e.key);
    if (isMobile) setMobileOpen(false);
  };

  // Pick the LONGEST matching path, not the first one. Several menu
  // items share parent prefixes — Paths.grnList = /depo/goods-receipt
  // and Paths.stoReceipt = /depo/goods-receipt/sto-receipt both match
  // when the operator is on the STO Receipt page. Plain `.find()`
  // returns whichever appears first in the menu array (usually the
  // parent), so the wrong row gets highlighted. Sorting matches by
  // length and taking the longest ensures the deepest match wins.
  const selectedKey = filteredMenuList
    .map((item) => item.path)
    .filter((path) => path && location.pathname.startsWith(path))
    .sort((a, b) => b.length - a.length)[0];

  const roleName = userDetails?.role_name || userDetails?.role?.role_name;

  const userMenuItems = [
    {
      key: "profile",
      // Read-only profile block so the operator can confirm who they are
      // signed in as, with which role + depot, after login.
      label: (
        <div style={{ padding: "4px 4px 8px", minWidth: 200, cursor: "default" }}>
          <Typography.Text strong style={{ display: "block", fontSize: 13 }}>
            {userDetails?.user_name || "User"}
          </Typography.Text>
          {userDetails?.user_email && (
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              {userDetails.user_email}
            </Typography.Text>
          )}
          {roleName && (
            <Tag color="blue" style={{ marginTop: 6, marginRight: 0 }}>{roleName}</Tag>
          )}
          {userDetails?.depot && (
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
              {userDetails.depot.depot_name || userDetails.depot.depot_code}
            </Typography.Text>
          )}
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      danger: true,
      onClick: handleLogout,
    },
  ];

  const searchOptions = searchValue
    ? filteredMenuList
        .filter((item) =>
          item.title.toLowerCase().includes(searchValue.toLowerCase())
        )
        .map((item) => ({
          value: item.path,
          label: (
            <Space>
              {item.icon ? React.createElement(item.icon, { style: { fontSize: 14, color: "#6b7280" } }) : null}
              <span>{item.title}</span>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>{item.group}</span>
            </Space>
          ),
        }))
    : [];

  const handleSearchSelect = (path) => {
    navigate(path);
    setSearchValue("");
  };

  const sidebarContent = (
    <>
      <div
        style={{
          padding: collapsed && !isMobile ? "16px 8px" : "16px",
          textAlign: "center",
          borderBottom: "1px solid #eef0f2",
        }}
      >
        {(!collapsed || isMobile) && userDetails?.depot?.company_logo ? (
          <img
            src={getFullImageUrl(userDetails.depot.company_logo)}
            alt="Logo"
            style={{ height: 32, objectFit: "contain", maxWidth: 140 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <Typography.Title
            level={5}
            style={{
              color: "#1e293b",
              margin: 0,
              fontSize: collapsed && !isMobile ? 12 : 16,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            {collapsed && !isMobile ? "RCM" : "RCM Depot"}
          </Typography.Title>
        )}
      </div>
      {(!collapsed || isMobile) && (
        <div style={{ padding: "12px 12px 4px" }}>
          <Input
            placeholder="Search menu..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            allowClear
            size="small"
            value={menuSearch}
            onChange={(e) => setMenuSearch(e.target.value)}
            style={{
              background: "#f5f7fa",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
            }}
            // Antd 5: style on the wrapper doesn't reach the inner
            // <input>, so text color was being inherited from the dark
            // sidebar theme → invisible. Use `styles.input` to colour
            // the actual input element.
            styles={{
              input: { color: "#1f2937", background: "transparent" },
            }}
          />
        </div>
      )}
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        items={filteredMenuItems}
        onClick={handleMenuClick}
        style={{ background: "transparent", borderRight: "none" }}
      />
    </>
  );

  const sidebarMargin = isMobile ? 0 : (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          width={SIDEBAR_WIDTH}
          collapsedWidth={SIDEBAR_COLLAPSED}
          style={{
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            background: "#ffffff",
            borderRight: "1px solid #eef0f2",
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          width={260}
          styles={{ body: { padding: 0, background: "#ffffff" }, header: { display: "none" } }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{ marginLeft: sidebarMargin, transition: "margin-left 0.2s" }}>
        <Header
          style={{
            padding: "0 20px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            zIndex: 99,
            height: HEADER_HEIGHT,
            lineHeight: `${HEADER_HEIGHT}px`,
          }}
        >
          <Space size={12}>
            <Button
              type="text"
              size="small"
              icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => isMobile ? setMobileOpen(true) : setCollapsed(!collapsed)}
              style={{ fontSize: 16, width: 32, height: 32 }}
            />
            {userDetails?.depot && (
              myDepots.length > 1 ? (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    selectable: true,
                    selectedKeys: [userDetails.depot.depot_code],
                    items: myDepots.map((d) => ({
                      key: d.depot_code,
                      label: d.depot_name || d.depot_code,
                    })),
                    onClick: ({ key }) => handleSwitchDepot(key),
                  }}
                >
                  <Tag
                    style={{
                      borderRadius: 4,
                      padding: "0 8px",
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#4361ee",
                      background: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      cursor: "pointer",
                    }}
                  >
                    {userDetails.depot.depot_name || userDetails.depot.depot_code}
                    <SwapOutlined style={{ marginLeft: 6 }} />
                  </Tag>
                </Dropdown>
              ) : (
                <Tag
                  style={{
                    borderRadius: 4,
                    padding: "0 8px",
                    fontWeight: 500,
                    fontSize: 12,
                    color: "#4361ee",
                    background: "#eef2ff",
                    border: "1px solid #c7d2fe",
                  }}
                >
                  {userDetails.depot.depot_name || userDetails.depot.depot_code}
                </Tag>
              )
            )}
          </Space>

          {/* Global search — hidden on small mobile */}
          <AutoComplete
            value={searchValue}
            options={searchOptions}
            onSelect={handleSearchSelect}
            onChange={setSearchValue}
            style={{ width: isMobile ? 160 : 260, flexShrink: 1 }}
            popupMatchSelectWidth={300}
          >
            <Input
              ref={searchRef}
              placeholder={isMobile ? "Search..." : "Search pages... (Ctrl+K)"}
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              allowClear
              size="small"
              style={{
                borderRadius: 6,
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                height: 32,
              }}
              styles={{
                input: { color: "#1f2937", background: "transparent" },
              }}
            />
          </AutoComplete>

          <Space size={8}>
            <Badge count={alertCount} size="small" offset={[-2, 2]}>
              <Button
                type="text"
                size="small"
                icon={<BellOutlined style={{ fontSize: 16 }} />}
                onClick={() => navigate(Paths.stockAlerts)}
                style={{ width: 32, height: 32 }}
              />
            </Badge>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: "pointer", marginLeft: 4 }}>
                <Avatar
                  size={28}
                  icon={<UserOutlined />}
                  style={{ background: "#4361ee" }}
                />
                {!isMobile && (
                  <div style={{ lineHeight: "normal" }}>
                    <Typography.Text strong style={{ display: "block", fontSize: 13, lineHeight: "16px" }}>
                      {userDetails?.user_name || "User"}
                    </Typography.Text>
                    {roleName && (
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", lineHeight: "14px" }}>
                        {roleName}
                      </Typography.Text>
                    )}
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ margin: 0 }}>
          {breadcrumbItems.length > 1 && (
            <div style={{ padding: "8px 24px 0" }}>
              <Breadcrumb items={breadcrumbItems} className="page-breadcrumb" />
            </div>
          )}
          <div className="depo-content">
            <AppRoutes loggedIn={loggedIn} />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
